/**
 * Webメールの AI `/ai`。
 *
 * `apps/api/src/routes/ops/ai.ts` の 6 本（プラットフォーム既定の参照・更新、
 * 組織一覧、組織詳細、組織の上書き、再同期）に対応する画面。API はあったが
 * **運営コンソールから触る手段が 1 つも無く**、共有プロバイダの切り替えも
 * 契約者のロックも DB 直打ちでしかできなかった。
 *
 * ## この画面の構造がそのまま統制になっている
 *
 *   - **プラットフォーム既定**（`ai_platform_defaults`）… 共有プロバイダ・
 *     モデル・既定の上限・選ばせてよいプロバイダの許可リスト。下位プランの
 *     契約者はここで決めた共有プロバイダで動く。
 *   - **共有 API キー**… 平文は返らない。**指紋だけ**を出す（本番と検証で
 *     同じ鍵かを目視で照合するため。マスクした生値は照合に使えない）。
 *   - **組織ごとの上書きとロック**… ロック中は契約者が変更できない。
 *     強制は RLS 側にも入っている（0048）ので、画面や API を 1 本足した
 *     だけでは穴が開かない。
 *
 * ## 「希望」と「実効」を必ず並べて出す
 *
 * `GET /ops/ai/orgs/:orgId` は `requested`（入っている値）と
 * `effective`（プランの制約を当てた実際に効く値）を両方返す。片方だけ
 * 見せると「設定したのに効いていない」の理由（プランが `aiAssistant=false`、
 * `aiCustomEndpoint=false` で共有側へ寄せられた、上限が切り詰められた）が
 * 画面から分からなくなる。**必ず 2 列で出す。**
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, Building2, KeyRound, Lock, Plus, RefreshCw, Save, Server, Sparkles, Trash2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DataTable,
  Input,
  KeyValue,
  PageHeader,
  Section,
  StatusBadge,
  SwitchRow,
  Textarea,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import {
  AI_BYO_PROVIDERS,
  AI_DATA_RESIDENCY,
  AI_FEATURE,
  AI_LIMIT_ACTION,
  AI_PRIVACY_LEVEL,
  AI_PROVIDER,
  AI_REGION,
  AI_SYNC_STATE,
  checkAiEndpointShape,
  getAiPreset,
  isAiProviderAllowed,
  labelOf,
  validateModelForPreset,
  type AiDataResidency,
  type AiPlatformDefaults,
  type AiPresetOrNone,
  type AiProvider,
  type OrgAiPolicy,
} from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { ConfirmTextDialog, FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { PageTabs } from '../components/page-tabs.js';
import {
  DetailSkeleton,
  ListEmpty,
  PermissionDenied,
  QueryError,
  RefetchingDot,
  errorMessageOf,
} from '../components/states.js';
import { RID } from './ids.js';
import type {
  OpsAiEndpoint,
  OpsAiOrgDetail,
  OpsAiOrgRow,
  OpsAiPlatformView,
  OpsAiPreset,
  OpsAiPresetModels,
} from '../types/api.js';

const routeApi = getRouteApi(RID.ai);

/** 一覧の 1 ページ。API は cursor ではなく limit/offset。 */
const PAGE_SIZE = 50;

/** BYO（自分の接続先を指定できる）プロバイダか。 */
function isByo(provider: AiProvider): boolean {
  return (AI_BYO_PROVIDERS as readonly string[]).includes(provider);
}

export function AiPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canReadPlatform = usePermission(OPS_PERMISSION.settingsRead);
  const canReadOrgs = usePermission(OPS_PERMISSION.orgRead);

  if (!canReadPlatform && !canReadOrgs) return <PermissionDenied />;

  // 権限の無いタブは出さない（押しても 403 になるだけ）。
  const tabs = [
    ...(canReadPlatform
      ? [
          { value: 'platform' as const, label: '共有の設定', icon: Sparkles },
          // 接続先の在庫も `ops.settings.read`（API 側と同じ権限）。
          { value: 'endpoints' as const, label: '接続先', icon: Server },
        ]
      : []),
    ...(canReadOrgs ? [{ value: 'orgs' as const, label: '契約組織ごと', icon: Building2 }] : []),
  ];
  const tab = tabs.some((t) => t.value === search.tab) ? search.tab : (tabs[0]?.value ?? 'platform');

  return (
    <>
      <PageHeader
        title="Webメールの AI"
        description="共有プロバイダ・モデル・上限の既定と、契約組織ごとの上書き。運営が入れた値は契約者から変更できません（ロック）。"
      />

      {tabs.length > 1 ? (
        <PageTabs
          value={tab}
          onChange={(next) => void navigate({ search: (prev) => ({ ...prev, tab: next }) })}
          tabs={tabs}
        />
      ) : null}

      {tab === 'orgs' ? <OrgsTab /> : null}
      {tab === 'endpoints' ? <EndpointsTab /> : null}
      {tab === 'platform' ? <PlatformTab /> : null}
    </>
  );
}

/* ------------------------------------------------------------ 接続先の在庫 */

/**
 * AI 接続先の在庫（`ai_provider_endpoints`）。
 *
 * **ルーティングの候補そのもの。** 契約者が自分の接続先を持てない
 * プランでは、ここに 1 件も無いと AI が 1 件も繋がらない。API は在ったのに
 * 画面が無く、追加も無効化も psql でしかできなかった。
 *
 * 画面が出すのは「なぜその接続先が選ばれないか」を追える情報に絞る:
 * 有効・健全性・レジデンシー・優先度・鍵の有無。**鍵は指紋だけ**で、
 * 平文はサーバーからも返らない（登録・差し替えだけができる）。
 */
function EndpointsTab(): React.ReactElement {
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.settingsWrite);
  const [editTarget, setEditTarget] = React.useState<OpsAiEndpoint | null>(null);
  const [addOpen, setAddOpen] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<OpsAiEndpoint | null>(null);

  const query = useQuery({
    queryKey: qk.aiEndpoints(),
    queryFn: () => api.get<{ items: OpsAiEndpoint[] }>('/ai/endpoints'),
  });

  const probe = useMutation({
    mutationFn: () => api.post<{ accepted: boolean }>('/ai/endpoints/health-check', {}),
  });

  const rows = query.data?.items ?? [];
  const usable = rows.filter((r) => r.enabled && r.health !== 'down' && r.hasKey);
  const missingKey = rows.filter((r) => r.enabled && !r.hasKey);

  const columns: Array<DataTableColumn<OpsAiEndpoint>> = [
    {
      id: 'code',
      header: '識別名',
      cell: (r) => (
        <div className="min-w-0">
          <span className="block truncate font-mono text-xs font-medium">{r.code}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {/*
              プリセット（0060）を出す。**出さないと「なぜ法域が空なのか」が
              画面から追えない**（アグリゲータだから、が答え）。
            */}
            {presetLabelOf(r.preset) ?? labelOf(r.provider)}
            {r.model ? ` / ${r.model}` : ''}
          </span>
        </div>
      ),
    },
    {
      id: 'endpoint',
      header: '接続先',
      hideBelow: 'lg',
      cell: (r) => <span className="font-mono text-xs">{r.endpoint}</span>,
    },
    {
      id: 'region',
      header: 'リージョン',
      hideBelow: 'md',
      cell: (r) => (
        <div className="min-w-0">
          <span className="block text-xs">{r.region}</span>
          {/*
            **`null` を空欄にしない。** 法域が確定しない在庫（アグリゲータ）は
            レジデンシー要件を持つ組織から必ず外れる。空欄だと「未設定なので
            あとで入れればよい」に見えるが、実際は入れられない（API と DB の
            CHECK が拒否する）。
          */}
          <span className="block text-xs text-muted-foreground">
            {r.dataResidency ?? '法域不明（処理先を公開しないサービス）'}
          </span>
        </div>
      ),
    },
    {
      id: 'state',
      header: '状態',
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          <Badge variant={r.enabled ? 'success' : 'muted'} size="sm">
            {r.enabled ? '有効' : '無効'}
          </Badge>
          <Badge
            variant={r.health === 'healthy' ? 'success' : r.health === 'degraded' ? 'warning' : 'destructive'}
            size="sm"
          >
            {AI_ENDPOINT_HEALTH_LABEL[r.health] ?? r.health}
          </Badge>
          {/*
            **鍵が無い接続先は候補に残っていても 401 で落ちる。**
            「有効なのに繋がらない」の原因がここで分かるようにする。
          */}
          {r.hasKey ? null : (
            <Badge variant="destructive" size="sm">
              鍵が無い
            </Badge>
          )}
        </div>
      ),
    },
    { id: 'priority', header: '優先度', align: 'right', hideBelow: 'md', cell: (r) => formatCount(r.priority) },
    {
      id: 'checked',
      header: '最終疎通',
      hideBelow: 'lg',
      cell: (r) =>
        r.lastCheckedAt ? (
          <div className="min-w-0">
            <span className="block text-xs">{formatDateTime(r.lastCheckedAt)}</span>
            {r.lastError ? <span className="block truncate text-xs text-destructive">{r.lastError}</span> : null}
          </div>
        ) : (
          '未実施'
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-28',
      cell: (r) =>
        canWrite ? (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditTarget(r)}>
              編集
            </Button>
            <Button variant="ghost" size="icon" aria-label="削除" onClick={() => setDeleteTarget(r)}>
              <Trash2 className="size-4 text-destructive" aria-hidden />
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <RefetchingDot active={query.isFetching && !query.isLoading} />
        {canWrite ? (
          <>
            <Button variant="outline" size="sm" onClick={() => probe.mutate()} loading={probe.isPending}>
              <Activity aria-hidden />
              いま疎通を確認する
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus aria-hidden />
              接続先を追加
            </Button>
          </>
        ) : null}
      </div>

      {probe.isSuccess ? (
        <Alert variant="success">
          <AlertDescription>
            疎通確認を積みました。結果が入るまで数十秒かかります（この表を開き直すと反映されます）。
          </AlertDescription>
        </Alert>
      ) : null}

      {rows.length === 0 && !query.isLoading ? (
        <Alert variant="warning">
          <AlertTitle>接続先が 1 件も登録されていません</AlertTitle>
          <AlertDescription>
            自分の接続先を指定できないプランの契約者は、この在庫から選ばれた接続先で動きます。
            0 件のままだと、共有プロバイダを設定していても AI は 1 件も繋がりません。
          </AlertDescription>
        </Alert>
      ) : usable.length === 0 ? (
        <Alert variant="destructive">
          <AlertTitle>いま選べる接続先がありません</AlertTitle>
          <AlertDescription>
            有効・健全・鍵ありの 3 つが揃った接続先が 0 件です。
            {missingKey.length > 0 ? `${formatCount(missingKey.length)} 件は鍵が入っていません。` : ''}
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="接続先" />}
      />

      <EndpointDialog
        endpoint={addOpen ? null : editTarget}
        open={addOpen || editTarget !== null}
        onOpenChange={(next) => {
          if (!next) {
            setAddOpen(false);
            setEditTarget(null);
          }
        }}
        onSaved={() => void qc.invalidateQueries({ queryKey: qk.aiEndpoints() })}
      />

      <DeleteEndpointDialog
        endpoint={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => void qc.invalidateQueries({ queryKey: qk.aiEndpoints() })}
      />
    </div>
  );
}

/** 接続先の健全性の訳語。値は `AI_ENDPOINT_HEALTH`（shared）。 */
const AI_ENDPOINT_HEALTH_LABEL: Record<string, string> = {
  healthy: '正常',
  degraded: '不安定',
  down: '停止',
};

/**
 * プリセットの表示名。プリセットでなければ null（呼び出し側がプロバイダ名へ倒す）。
 * **知らない値でも落ちない**（`getAiPreset` は throw しない）。
 */
function presetLabelOf(preset: string): string | null {
  return getAiPreset(preset)?.label ?? null;
}

/**
 * 接続先の追加・編集。
 *
 * **`code` は作成時にしか入力させない。** API の patch スキーマが
 * `code` を除いているのは、運営の手順書や監視の紐付けが切れるため。
 * 画面でも編集時は読み取り専用にして、送らない。
 *
 * ## プリセット（0060）を先頭に置く理由
 *
 * OpenRouter 等のアグリゲータは `provider` / `endpoint` / `dataResidency` の
 * 3 つに**同時に**制約が掛かる（OpenAI 互換であること・URL は定義側が正・
 * 法域は申告できない）。これを別々の入力欄として並べると、運営は 3 回
 * 400 を踏んでからでないと正しい組み合わせに辿り着けない。
 * プリセットを選んだ時点で残りを画面が確定させ、`checkAiEndpointShape()`
 * （**API と同じ純関数**）で送信前に理由を出す。
 */
function EndpointDialog({
  endpoint,
  open,
  onOpenChange,
  onSaved,
}: {
  endpoint: OpsAiEndpoint | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}): React.ReactElement {
  const editing = endpoint !== null;
  const [code, setCode] = React.useState('');
  const [provider, setProvider] = React.useState<AiProvider>('openai_compat');
  const [region, setRegion] = React.useState<string>('jp-east');
  /** `''` は「法域を申告しない」（＝サーバーへ `null` を送る）。 */
  const [dataResidency, setDataResidency] = React.useState<string>('jp');
  const [preset, setPreset] = React.useState<string>('');
  const [endpointUrl, setEndpointUrl] = React.useState('');
  const [model, setModel] = React.useState('');
  const [priority, setPriority] = React.useState('100');
  const [enabled, setEnabled] = React.useState(true);
  const [healthCheckPath, setHealthCheckPath] = React.useState('');
  const [credentialRef, setCredentialRef] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [apiKey, setApiKey] = React.useState('');
  /** 取得したモデル一覧（null = まだ引いていない）。 */
  const [models, setModels] = React.useState<OpsAiPresetModels | null>(null);

  /*
   * プリセット表はサーバーが持っている定数（`GET /ops/ai/presets`）を正とする。
   * shared の定数を直接読んでも同じ値だが、説明とドキュメント URL を
   * サーバーから受け取る形にしておくと、表を足したときに画面の改修が要らない。
   */
  const presetsQuery = useQuery({
    queryKey: qk.aiPresets(),
    queryFn: () => api.get<{ items: OpsAiPreset[] }>('/ai/presets'),
    staleTime: 10 * 60_000,
  });
  const presets = presetsQuery.data?.items ?? [];
  const currentPreset = presets.find((p) => p.key === preset) ?? null;

  React.useEffect(() => {
    if (!open) return;
    setCode(endpoint?.code ?? '');
    setProvider((endpoint?.provider ?? 'openai_compat') as AiProvider);
    setRegion(endpoint?.region ?? 'jp-east');
    /*
     * **`null` を `'jp'` に読み替えない。** アグリゲータの在庫は法域が
     * `null` で保存されており、既定値へ倒すと「画面は法域不明と出ているのに
     * 送信すると 400」という食い違いになる（実際に踏んだ）。
     * 新規作成のときだけ `jp` を初期値にする。
     */
    setDataResidency(endpoint ? (endpoint.dataResidency ?? '') : 'jp');
    setPreset(endpoint?.preset ?? '');
    setEndpointUrl(endpoint?.endpoint ?? '');
    setModel(endpoint?.model ?? '');
    setPriority(String(endpoint?.priority ?? 100));
    setEnabled(endpoint?.enabled ?? true);
    setHealthCheckPath(endpoint?.healthCheckPath ?? '');
    setCredentialRef(endpoint?.credentialRef ?? '');
    setNotes(endpoint?.notes ?? '');
    // 鍵は毎回空から。既存値はサーバーからも返らない（指紋だけ）。
    setApiKey('');
    setModels(null);
  }, [open, endpoint]);

  /**
   * プリセットを選び直したときに、連動する項目をその場で確定させる。
   * **ここで確定させないと、運営は 400 を 3 回踏むことになる**
   * （プロバイダ・URL・法域の 3 つに同時に制約が掛かるため）。
   */
  const changePreset = (next: string): void => {
    setPreset(next);
    setModels(null);
    const def = presets.find((p) => p.key === next) ?? null;
    if (!def) return;
    // プリセットは全部 OpenAI 互換（`AI_PRESET_PROVIDER`）。
    setProvider('openai_compat');
    if (def.baseUrl !== null) setEndpointUrl(def.baseUrl);
    else if (endpointUrl === (presets.find((p) => p.key === preset)?.baseUrl ?? '')) setEndpointUrl('');
    setDataResidency(def.residencyUnknown ? '' : dataResidency === '' ? 'jp' : dataResidency);
  };

  /* -------------------------------------------------------- モデル一覧 */

  const fetchModels = useMutation({
    mutationFn: () =>
      api.post<OpsAiPresetModels>(`/ai/presets/${preset}/models`, {
        // 固定型ではサーバーが定義側の baseUrl を使う。送っても無視される。
        baseUrl: endpointUrl.trim(),
        // **保存されない。** この 1 回の取得にだけ使われる（API のコメント参照）。
        apiKey: apiKey.trim(),
        refresh: true,
      }),
    onSuccess: (res) => setModels(res),
  });

  const residencyValue = dataResidency === '' ? null : (dataResidency as AiDataResidency);
  /** **API と同じ純関数**で送信前に理由を出す（400 を踏ませない）。 */
  const shapeError = checkAiEndpointShape({
    provider,
    endpoint: endpointUrl.trim(),
    preset: preset as AiPresetOrNone,
    dataResidency: residencyValue,
  });
  /*
   * モデル名の形の検査も**サーバーと同じ純関数**。`vendor/model` 形の
   * サービスに `/` の無い名前を入れると、保存はできても実行時に 4xx になる
   * （在庫の登録では上流を叩かないので、その場では気付けない）。
   */
  const presetDefinition = getAiPreset(preset);
  const modelCheck =
    presetDefinition && model.trim() !== ''
      ? validateModelForPreset(presetDefinition, model.trim())
      : { ok: true, message: null };

  const save = useMutation({
    mutationFn: () => {
      const base = {
        provider,
        region,
        endpoint: endpointUrl.trim(),
        model: model.trim(),
        priority: Number(priority || '100'),
        enabled,
        // **`null` を送る。** 空文字ではない（enum に空文字は無い）。
        dataResidency: residencyValue,
        preset,
        healthCheckPath: healthCheckPath.trim(),
        credentialRef: credentialRef.trim(),
        notes: notes.trim(),
        // 空文字は「鍵を消す」の意味を持つので、**入力があったときだけ送る**。
        ...(apiKey.trim().length > 0 ? { apiKey: apiKey.trim() } : {}),
      };
      return editing
        ? api.patch(`/ai/endpoints/${endpoint.id}`, base)
        : api.post<{ id: string }>('/ai/endpoints', { ...base, code: code.trim() });
    },
    onSuccess: () => {
      onSaved();
      onOpenChange(false);
    },
  });

  const codeValid = editing || /^[a-z0-9][a-z0-9-]*$/.test(code.trim());

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? `接続先「${endpoint.code}」を編集する` : '接続先を追加する'}
      description="ルーティングの候補になります。保存すると、この在庫で動いている契約組織へ再同期が積まれます。"
      submitLabel={editing ? '保存する' : '追加する'}
      pending={save.isPending}
      error={save.error}
      disabled={!codeValid || shapeError !== null || !modelCheck.ok}
      onSubmit={() => save.mutate()}
      wide
    >
      <Field
        label="サービス（プリセット）"
        hint={
          currentPreset
            ? currentPreset.description
            : 'アグリゲータ（OpenRouter 等）や自社配備の LiteLLM / vLLM / Azure OpenAI を選ぶと、接続先 URL と法域の扱いが自動で決まります。'
        }
      >
        <NativeSelect
          value={preset}
          onChange={changePreset}
          options={[
            { value: '', label: '使わない（プロバイダと URL を直接指定）' },
            ...presets.map((p) => ({ value: p.key, label: p.label })),
          ]}
        />
      </Field>

      {currentPreset ? (
        <Alert variant={currentPreset.residencyUnknown ? 'warning' : 'info'}>
          <AlertDescription className="space-y-1">
            <span className="block">
              {currentPreset.residencyUnknown
                ? '実際の処理先（国・事業者）を公開しないサービスです。法域は申告できず、この在庫はデータレジデンシーを要求している組織からは選ばれません。'
                : '配備先は運営が把握しているサービスです。法域を指定してください。'}
            </span>
            {/* 鍵の取り方は向こうのページが正。運営がここで詰まらないように出す。 */}
            <a
              className="inline-block underline underline-offset-2"
              href={currentPreset.docsUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              {currentPreset.label} の API キーについて
            </a>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="識別名（code）"
          required
          hint={editing ? '作成後は変更できません（手順書や監視の紐付けが切れるため）。' : '英小文字・数字・ハイフン。'}
          error={!codeValid && code.trim().length > 0 ? '英小文字・数字・ハイフンで入力してください。' : undefined}
        >
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase())}
            disabled={editing}
            placeholder="openai-jp-east"
          />
        </Field>
        <Field
          label="プロバイダ"
          required
          hint={currentPreset ? 'プリセットは OpenAI 互換に固定されます。' : undefined}
        >
          <NativeSelect
            value={provider}
            onChange={(v) => setProvider(v as AiProvider)}
            // `none` は在庫として登録できない（API の zod が弾く）。
            options={AI_PROVIDER.filter((p) => p !== 'none').map((p) => ({ value: p, label: labelOf(p) }))}
            disabled={currentPreset !== null}
          />
        </Field>
        <Field label="リージョン" required hint="契約者の希望リージョンとの距離で順位が決まります。">
          <NativeSelect value={region} onChange={setRegion} options={AI_REGION.map((r) => ({ value: r, label: r }))} />
        </Field>
        <Field
          label="データレジデンシー"
          required={!currentPreset?.residencyUnknown}
          hint={
            currentPreset?.residencyUnknown
              ? '申告できません（処理先が公開されていないため）。'
              : '契約者が要求したレジデンシーに合わない接続先は選ばれません。'
          }
        >
          <NativeSelect
            value={dataResidency}
            onChange={setDataResidency}
            options={
              currentPreset?.residencyUnknown
                ? [{ value: '', label: '法域不明（指定できません）' }]
                : AI_DATA_RESIDENCY.map((r) => ({ value: r, label: r }))
            }
            disabled={currentPreset?.residencyUnknown ?? false}
          />
        </Field>
      </div>

      <Field
        label="エンドポイント URL"
        required
        hint={
          currentPreset && !currentPreset.selfHosted
            ? 'このサービスは接続先が決まっています（打ち間違いを防ぐため入力できません）。'
            : '保存前に SSRF の検査を通ります（内部アドレスは登録できません）。'
        }
      >
        <Input
          value={endpointUrl}
          onChange={(e) => setEndpointUrl(e.target.value)}
          placeholder="https://api.example.jp"
          disabled={currentPreset !== null && !currentPreset.selfHosted}
        />
      </Field>

      {/*
        **送信前に理由を出す。** 判定は API と同じ `checkAiEndpointShape()`。
        画面側で別の条件を書くと、片方だけ直したときに「画面は通るのに 400」
        （またはその逆）になる。
      */}
      {shapeError ? (
        <Alert variant="destructive">
          <AlertDescription>{shapeError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="モデル"
          hint="空なら組織の指定かプラットフォーム既定に従います。"
          error={modelCheck.ok ? undefined : modelCheck.message}
        >
          <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="claude-haiku-4-5" />
        </Field>
        <Field label="優先度" hint="小さいほど優先。同点のときの最終手段です。">
          <Input
            value={priority}
            onChange={(e) => setPriority(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
          />
        </Field>
        <Field label="疎通確認のパス" hint="例: /v1/models。空なら URL そのものを叩きます。">
          <Input
            value={healthCheckPath}
            onChange={(e) => setHealthCheckPath(e.target.value)}
            placeholder="/v1/models"
          />
        </Field>
        <Field
          label="資格情報の参照名"
          hint="空ならプロバイダと同名の共有鍵を使います。複数の接続先で同じ鍵を共有できます。"
        >
          <Input value={credentialRef} onChange={(e) => setCredentialRef(e.target.value)} placeholder="openai" />
        </Field>
      </div>

      <SwitchRow
        label="この接続先を有効にする"
        description="無効にすると候補から外れます。削除しなくても止められます。"
        checked={enabled}
        onCheckedChange={setEnabled}
      />

      <Field
        label="API キー"
        hint={
          editing
            ? `入力したときだけ差し替えます。空のままなら現在の鍵を維持します${
                endpoint.fingerprint ? `（現在の指紋: ${endpoint.fingerprint}）` : '（未登録）'
              }。`
            : '空のままでも作成できます（あとから差し替えられます）。'
        }
      >
        <Input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      </Field>

      {/*
        モデル一覧（`POST /ops/ai/presets/:key/models`）。

        **GET ではなく POST なのは鍵を伴うから**（GET のクエリ文字列に鍵を
        載せると前段のアクセスログとブラウザ履歴に残る）。入力中の鍵を
        そのまま使えるので、**保存する前に**「その鍵でどのモデルが見えるか」を
        確認できる。サーバーは受け取った鍵を保存も監査もしない。

        取得できなくてもエラーにしない（`manualEntry`）。一覧が引けないことは
        手入力へのフォールバックであって、登録できない理由ではない。
      */}
      {currentPreset ? (
        <div className="space-y-2 rounded-md border border-border p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium">{currentPreset.label} のモデル一覧</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={fetchModels.isPending}
              disabled={!currentPreset.hasModelList || endpointUrl.trim().length === 0}
              onClick={() => fetchModels.mutate()}
            >
              <RefreshCw aria-hidden />
              一覧を取得する
            </Button>
          </div>

          {!currentPreset.hasModelList ? (
            <p className="text-[12px] text-muted-foreground">
              このサービスは互換のモデル一覧を提供していません（Azure OpenAI はデプロイ名を直接指定します）。
              モデル名は上の欄へ直接入力してください。
            </p>
          ) : null}

          {fetchModels.isError ? (
            <Alert variant="destructive">
              <AlertDescription>{errorMessageOf(fetchModels.error)}</AlertDescription>
            </Alert>
          ) : null}

          {models ? (
            models.models.length > 0 ? (
              <div className="space-y-1">
                <NativeSelect
                  value={model}
                  onChange={setModel}
                  options={[
                    { value: '', label: `— ${formatCount(models.models.length)} 件から選ぶ —` },
                    ...models.models.map((m) => ({
                      value: m.id,
                      label: m.contextLength ? `${m.id}（${formatCount(m.contextLength)} トークン）` : m.id,
                    })),
                  ]}
                />
                <p className="text-[12px] text-muted-foreground">
                  {models.source === 'cache' ? 'キャッシュ' : '取得'}:{' '}
                  {models.fetchedAt ? formatDateTime(models.fetchedAt) : '—'}
                </p>
              </div>
            ) : (
              <p className="text-[12px] text-muted-foreground">
                {models.message ?? '一覧は空でした。モデル名は上の欄へ直接入力してください。'}
              </p>
            )
          ) : null}
        </div>
      ) : null}

      <Field label="メモ">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </Field>
    </FormDialog>
  );
}

/**
 * 接続先の削除。
 *
 * **鍵は道連れにしない**（API の実装がそうなっている）。同じ
 * `credential_ref` を別の接続先が使っていることがあり、消すと
 * そちらが黙って 401 で落ちるため。
 */
function DeleteEndpointDialog({
  endpoint,
  onClose,
  onDeleted,
}: {
  endpoint: OpsAiEndpoint | null;
  onClose: () => void;
  onDeleted: () => void;
}): React.ReactElement {
  const remove = useMutation({
    mutationFn: () => api.del(`/ai/endpoints/${endpoint?.id ?? ''}`),
    onSuccess: () => {
      onDeleted();
      onClose();
    },
  });

  return (
    <ConfirmTextDialog
      open={endpoint !== null}
      onOpenChange={(next) => !next && onClose()}
      title="接続先を削除する"
      description="この在庫で動いていた契約組織は、次に選べる接続先へ寄せ直されます。止めたいだけなら削除ではなく「無効」にしてください。鍵は残ります（他の接続先が同じ鍵を使っていることがあるため）。"
      confirmText={endpoint?.code ?? ''}
      confirmLabel="削除する"
      pending={remove.isPending}
      error={remove.error}
      onConfirm={() => remove.mutate()}
    />
  );
}

/* ------------------------------------------------ プラットフォーム既定 */

function PlatformTab(): React.ReactElement {
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.settingsWrite);
  const [draft, setDraft] = React.useState<AiPlatformDefaults | null>(null);
  const [resynced, setResynced] = React.useState<number | null>(null);
  const [keyTarget, setKeyTarget] = React.useState<AiProvider | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<AiProvider | null>(null);

  const query = useQuery({
    queryKey: qk.aiPlatform(),
    queryFn: () => api.get<OpsAiPlatformView>('/ai/platform'),
  });

  // サーバーの値が来たら編集用の控えを作る。以後は編集中の値を優先する。
  React.useEffect(() => {
    if (query.data && draft === null) setDraft(query.data.defaults);
  }, [query.data, draft]);

  const save = useMutation({
    mutationFn: () => api.put<{ ok: boolean; resynced: number }>('/ai/platform', draft),
    onSuccess: (res) => {
      setResynced(res.resynced);
      void qc.invalidateQueries({ queryKey: ['ops', 'ai'] });
    },
  });

  if (query.isError) return <QueryError error={query.error} onRetry={() => void query.refetch()} />;
  if (query.isLoading || !draft || !query.data) return <DetailSkeleton />;

  const allowed = draft.allowedProviders;
  /*
   * サーバーは「共有プロバイダが許可リストに無い」を 400 で弾く。
   * 保存してから怒られるのではなく、先に止めて理由を出す。
   */
  const providerAllowed = isAiProviderAllowed(draft, draft.provider);

  const toggleAllowed = (provider: AiProvider, on: boolean): void => {
    const next = on ? [...allowed, provider] : allowed.filter((p) => p !== provider);
    setDraft({ ...draft, allowedProviders: next });
  };

  return (
    <div className="space-y-6">
      <Alert>
        <AlertTitle>ここで決めた共有プロバイダが「下位プランの既定」になります</AlertTitle>
        <AlertDescription>
          接続先を自分で指定できないプラン（<code className="font-mono">aiCustomEndpoint=false</code>）の契約者は、何を設定していても
          この共有プロバイダに寄せられます。共有プロバイダ・モデル・共有鍵を変えると、有効な組織すべてへ
          同期が積み直されます。
        </AlertDescription>
      </Alert>

      {resynced !== null ? (
        <Alert variant="success">
          <AlertDescription>
            保存しました。{formatCount(resynced)} 組織へ再同期を積みました
            {resynced === 0 ? '（内容が変わらないため 0 件です）' : ''}。
          </AlertDescription>
        </Alert>
      ) : null}

      {/* description は string なので Markdown 記法を書くと ` がそのまま画面に出る。 */}
      <Section title="共有の設定" description="ai_platform_defaults の 1 行。全契約者の既定になります。">
        <div className="space-y-4 rounded-lg border border-border bg-card p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="共有プロバイダ"
              hint="「提供しない」は共有の接続先を配らない設定です。契約者が自分の接続先を持てるプランには影響しません。"
              error={providerAllowed ? undefined : '許可リストに含まれていないプロバイダは共有に選べません。'}
            >
              <NativeSelect
                value={draft.provider}
                onChange={(v) => setDraft({ ...draft, provider: v as AiProvider })}
                disabled={!canWrite}
                options={AI_PROVIDER.map((p) => ({ value: p, label: labelOf(p) }))}
              />
            </Field>
            <Field label="共有モデル" hint="組織側が空のときに使われます。">
              <Input
                value={draft.model}
                onChange={(e) => setDraft({ ...draft, model: e.target.value })}
                disabled={!canWrite}
                placeholder="claude-haiku-4-5"
              />
            </Field>
            <Field label="既定の月間トークン上限" hint="0 は無制限。組織側が 0 のときに当てます。">
              <Input
                value={String(draft.defaultMonthlyTokenCap)}
                onChange={(e) =>
                  setDraft({ ...draft, defaultMonthlyTokenCap: Number(e.target.value.replace(/[^0-9]/g, '') || '0') })
                }
                inputMode="numeric"
                disabled={!canWrite}
              />
            </Field>
            <Field label="既定のプライバシー水準" hint="どこまでを LLM に渡してよいか。">
              <NativeSelect
                value={draft.defaultPrivacyLevel}
                onChange={(v) => setDraft({ ...draft, defaultPrivacyLevel: v as AiPlatformDefaults['defaultPrivacyLevel'] })}
                disabled={!canWrite}
                options={AI_PRIVACY_LEVEL.map((p) => ({ value: p, label: labelOf(p) }))}
              />
            </Field>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              選ばせてよいプロバイダ
            </p>
            {/*
             * **空＝制限なし。** 「全部禁止」ではない（`isAiProviderAllowed`）。
             * 空を全部禁止と読む実装にすると、行を作った直後に全社の AI が
             * 黙って止まる。画面でもその意味をそのまま書いておく。
             */}
            <div className="flex flex-wrap gap-3">
              {AI_PROVIDER.filter((p) => p !== 'none').map((p) => (
                <label key={p} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={allowed.includes(p)}
                    onChange={(e) => toggleAllowed(p, e.target.checked)}
                    disabled={!canWrite}
                    className="size-4 rounded border-border"
                  />
                  {labelOf(p)}
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-[12px] text-muted-foreground">
              1 つも選ばなければ制限なし（全プロバイダを選べます）。
            </p>
          </div>

          <Field label="運営メモ" hint="契約者には見えません。">
            <Textarea
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              rows={2}
              disabled={!canWrite}
            />
          </Field>

          {save.isError ? <QueryError error={save.error} /> : null}

          {canWrite ? (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setDraft(query.data.defaults)}>
                取り消す
              </Button>
              <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!providerAllowed}>
                <Save aria-hidden />
                保存する
              </Button>
            </div>
          ) : null}
        </div>
      </Section>

      <Section
        title="共有の API キー"
        description="運営が原価を持つ分の鍵。平文は保存後に二度と取り出せません（指紋だけを表示します）。"
      >
        <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {query.data.credentials.map((c) => (
            <div key={c.provider} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-sm">
              <span className="w-40 shrink-0 font-medium">{labelOf(c.provider)}</span>
              {c.hasKey ? (
                <>
                  <StatusBadge status="active" size="sm" label="登録済み" tone="success" />
                  <span className="font-mono text-xs text-muted-foreground">{c.fingerprint}</span>
                </>
              ) : (
                <span className="text-xs text-muted-foreground">未登録</span>
              )}
              {canWrite ? (
                <span className="ml-auto flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => setKeyTarget(c.provider)}>
                    <KeyRound aria-hidden />
                    {c.hasKey ? '差し替える' : '登録する'}
                  </Button>
                  {c.hasKey ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="鍵を削除する"
                      onClick={() => setDeleteTarget(c.provider)}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  ) : null}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </Section>

      <SharedKeyDialog provider={keyTarget} onClose={() => setKeyTarget(null)} />
      <DeleteSharedKeyDialog provider={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </div>
  );
}

/** 共有鍵の登録・差し替え（`PUT /ai/platform` の `sharedApiKey`）。 */
function SharedKeyDialog({
  provider,
  onClose,
}: {
  provider: AiProvider | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [apiKey, setApiKey] = React.useState('');

  React.useEffect(() => {
    if (provider) setApiKey('');
  }, [provider]);

  const mut = useMutation({
    mutationFn: () =>
      api.put('/ai/platform', { sharedApiKey: { provider: provider ?? 'none', apiKey: apiKey.trim() } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'ai'] });
      onClose();
    },
  });

  return (
    <FormDialog
      open={provider !== null}
      onOpenChange={(next) => !next && onClose()}
      title={`${provider ? labelOf(provider) : ''} の共有鍵`}
      description="保存すると暗号化して保管します。以後この画面には指紋しか出ません。"
      submitLabel="保存する"
      pending={mut.isPending}
      error={mut.error}
      disabled={apiKey.trim().length === 0}
      onSubmit={() => mut.mutate()}
    >
      <Field label="API キー" required hint="貼り付けたら、元のコピー元（チャット等）からは必ず消してください。">
        <Input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      </Field>
    </FormDialog>
  );
}

/** 共有鍵の削除（`sharedApiKey.apiKey` に空文字を送る）。 */
function DeleteSharedKeyDialog({
  provider,
  onClose,
}: {
  provider: AiProvider | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.put('/ai/platform', { sharedApiKey: { provider: provider ?? 'none', apiKey: '' } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'ai'] });
      onClose();
    },
  });

  return (
    <ConfirmTextDialog
      open={provider !== null}
      onOpenChange={(next) => !next && onClose()}
      title="共有鍵を削除する"
      description="この鍵で動いていた契約者の AI は次の同期で止まります。差し替えるだけなら削除ではなく「差し替える」を使ってください。"
      confirmText={provider ?? ''}
      confirmLabel="削除する"
      pending={mut.isPending}
      error={mut.error}
      onConfirm={() => mut.mutate()}
    />
  );
}

/* ------------------------------------------------------- 契約組織ごと */

function OrgsTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const [offset, setOffset] = React.useState(0);

  // 絞り込みを変えたら先頭ページへ戻す（cursor 一覧と同じ作法）。
  const filterKey = `${search.syncState ?? ''}|${search.lockedOnly ? '1' : ''}`;
  React.useEffect(() => {
    setOffset(0);
  }, [filterKey]);

  const query = useQuery({
    queryKey: qk.aiOrgs({ syncState: search.syncState, lockedOnly: search.lockedOnly, offset }),
    queryFn: () =>
      api.get<{ items: OpsAiOrgRow[]; total: number }>('/ai/orgs', {
        limit: PAGE_SIZE,
        offset,
        syncState: search.syncState,
        // **true のときだけ送る。** サーバーは `z.coerce.boolean()` なので
        // `0` を送ると `Boolean('0') === true` になり、外したはずの絞り込みが効く。
        lockedOnly: search.lockedOnly ? 1 : undefined,
      }),
  });

  const columns: Array<DataTableColumn<OpsAiOrgRow>> = [
    {
      id: 'org',
      header: '契約組織',
      cell: (r) => (
        <button
          type="button"
          className="text-left font-medium hover:text-primary"
          onClick={() =>
            // 一覧の下に詳細を出すだけ（同じ画面）なので main を先頭へ戻さない。
            void navigate({ search: (prev) => ({ ...prev, orgId: r.organizationId }), resetScroll: false })
          }
        >
          {r.orgName}
        </button>
      ),
    },
    {
      id: 'enabled',
      header: '状態',
      width: 'w-28',
      cell: (r) =>
        r.enabled ? (
          <StatusBadge status="active" size="sm" dot label="有効" tone="success" />
        ) : (
          <StatusBadge status="disabled" size="sm" label="無効" tone="muted" />
        ),
    },
    {
      id: 'provider',
      header: 'プロバイダ',
      cell: (r) => (
        <div className="space-y-0.5">
          <span className="block">{labelOf(r.provider)}</span>
          <span className="block text-xs text-muted-foreground">{r.model === '' ? '共有の既定モデル' : r.model}</span>
        </div>
      ),
    },
    {
      id: 'cap',
      header: '月間上限',
      align: 'right',
      hideBelow: 'md',
      cell: (r) => (
        <div className="space-y-0.5">
          <span className="block">{r.monthlyTokenCap === 0 ? '無制限' : formatCount(r.monthlyTokenCap)}</span>
          {r.cappedAt ? <span className="block text-xs text-warning">到達 {formatDateTime(r.cappedAt)}</span> : null}
        </div>
      ),
    },
    {
      id: 'lock',
      header: 'ロック',
      hideBelow: 'md',
      cell: (r) =>
        r.lockedByOps ? (
          <Badge variant="warning" size="sm">
            <Lock aria-hidden />
            運営が固定
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      id: 'sync',
      header: '同期',
      cell: (r) => (
        <div className="space-y-0.5">
          <StatusBadge status={r.syncState} size="sm" dot />
          {r.syncError ? (
            <span className="block text-xs text-destructive">{r.syncError}</span>
          ) : r.syncedAt ? (
            <span className="block text-xs text-muted-foreground">{formatDateTime(r.syncedAt)}</span>
          ) : null}
        </div>
      ),
    },
  ];

  const total = query.data?.total ?? 0;
  const shown = query.data?.items.length ?? 0;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
        <RefetchingDot active={query.isFetching && !query.isLoading} />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={search.lockedOnly === true}
            onChange={(e) =>
              void navigate({ search: (prev) => ({ ...prev, lockedOnly: e.target.checked ? true : undefined }) })
            }
            className="size-4 rounded border-border"
          />
          運営が固定したものだけ
        </label>
        <NativeSelect
          value={search.syncState ?? ''}
          onChange={(v) =>
            void navigate({
              search: (prev) => ({ ...prev, syncState: v === '' ? undefined : (v as OpsAiOrgRow['syncState']) }),
            })
          }
          options={[
            { value: '', label: '同期状態: すべて' },
            ...AI_SYNC_STATE.map((s) => ({ value: s, label: labelOf(s) })),
          ]}
          className="h-9 w-56"
        />
      </div>

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(r) => r.organizationId}
        loading={query.isLoading}
        selectedKey={search.orgId ?? null}
        onRowClick={(r) =>
          // 一覧の下に詳細を出すだけ（同じ画面）なので main を先頭へ戻さない。
          void navigate({ search: (prev) => ({ ...prev, orgId: r.organizationId }), resetScroll: false })
        }
        emptyTitle="AI ポリシーを持つ組織がありません"
        emptyDescription="契約者が Webメールの AI を一度も設定していない場合、行そのものが作られません。"
        footer={
          <div className="flex items-center justify-between px-1 py-2 text-xs text-muted-foreground">
            <span>
              {total === 0 ? '0 件' : `${offset + 1}〜${offset + shown} 件 / 全 ${formatCount(total)} 件`}
            </span>
            <span className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0 || query.isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                前へ
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + shown >= total || query.isFetching}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                次へ
              </Button>
            </span>
          </div>
        }
      />

      {search.orgId ? (
        <OrgAiDetail
          orgId={search.orgId}
          onClose={() => void navigate({ search: (prev) => ({ ...prev, orgId: undefined }), resetScroll: false })}
        />
      ) : null}
    </>
  );
}

/** 選んだ組織の実効ポリシー・同期・使用量。 */
function OrgAiDetail({ orgId, onClose }: { orgId: string; onClose: () => void }): React.ReactElement {
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.orgWrite);
  const [editOpen, setEditOpen] = React.useState(false);

  const query = useQuery({
    queryKey: qk.aiOrg(orgId),
    queryFn: () => api.get<OpsAiOrgDetail>(`/ai/orgs/${orgId}`),
  });

  const resync = useMutation({
    mutationFn: () => api.post(`/ai/orgs/${orgId}/resync`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ops', 'ai'] }),
  });

  const d = query.data;

  return (
    <Section
      title={d ? `${d.organization.displayName} の AI 設定` : 'AI 設定'}
      className="mt-6"
      actions={
        <span className="flex gap-2">
          {d && canWrite ? (
            <>
              <Button variant="outline" size="sm" onClick={() => resync.mutate()} loading={resync.isPending}>
                <RefreshCw aria-hidden />
                再同期
              </Button>
              <Button size="sm" onClick={() => setEditOpen(true)}>
                運営として上書き
              </Button>
            </>
          ) : null}
          <Button variant="ghost" size="sm" onClick={onClose}>
            閉じる
          </Button>
        </span>
      }
    >
      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.isLoading ? <DetailSkeleton /> : null}

      {d ? (
        <div className="space-y-4">
          {d.lockedByOps ? (
            <Alert variant="warning">
              <AlertTitle>運営が固定しています</AlertTitle>
              <AlertDescription>
                契約者はこの組織の AI 設定を変更できません（RLS 側でも書き込みを止めています）。
                {d.opsNote ? <span className="mt-1 block">理由: {d.opsNote}</span> : null}
                {d.lockedAt ? (
                  <span className="mt-1 block text-xs">固定日時 {formatDateTime(d.lockedAt)}</span>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}

          {!d.plan.aiAssistant ? (
            <Alert>
              <AlertTitle>このプランでは AI 機能を提供していません</AlertTitle>
              <AlertDescription>
                プラン「{d.plan.code}」は <code className="font-mono">aiAssistant=false</code> です。設定を入れても実効値は無効のままになります
                （設定自体は消えないので、プランを上げれば戻ります）。
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="rounded-lg border border-border bg-card p-4">
            <KeyValue
              columns={3}
              items={[
                { key: 'プラン', value: `${d.plan.code}（上限 ${d.plan.aiMonthlyTokens === 0 ? '無制限' : formatCount(d.plan.aiMonthlyTokens)}）` },
                {
                  key: '接続先の指定',
                  value: d.plan.aiCustomEndpoint ? '契約者が指定できる' : '共有プロバイダに寄せる',
                },
                {
                  key: '同期',
                  value: (
                    <span className="text-sm">
                      <StatusBadge status={d.sync.state} size="sm" dot />
                      <span className="ml-2 text-xs text-muted-foreground">
                        {d.sync.error ?? (d.sync.syncedAt ? formatDateTime(d.sync.syncedAt) : '未同期')}
                      </span>
                    </span>
                  ),
                },
                {
                  key: `今月の利用（${d.usage.month}）`,
                  value: `${formatCount(d.usage.calls)} 回 / 入力 ${formatCount(d.usage.inputTokens)} ・ 出力 ${formatCount(d.usage.outputTokens)} トークン`,
                },
                { key: '失敗した呼び出し', value: formatCount(d.usage.failedCalls) },
                {
                  key: '専用の API キー',
                  value: d.credential.hasKey ? (
                    <span className="font-mono text-xs">{d.credential.fingerprint}</span>
                  ) : (
                    '未登録（共有鍵で動作）'
                  ),
                },
              ]}
            />
          </div>

          {/*
           * **希望と実効を並べる。** どちらか片方だけを出すと、
           * 「設定したのに効いていない」の理由が画面から消える。
           */}
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-panel text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">項目</th>
                  <th className="px-3 py-2 text-left">設定値（希望）</th>
                  <th className="px-3 py-2 text-left">実効値（プラン適用後）</th>
                </tr>
              </thead>
              <tbody>
                {policyRows(d.requested, d.effective).map((row) => (
                  <tr key={row.key} className="border-t border-border">
                    <td className="px-3 py-2 text-muted-foreground">{row.key}</td>
                    <td className="px-3 py-2">{row.requested}</td>
                    <td className={row.differs ? 'px-3 py-2 text-warning' : 'px-3 py-2'}>{row.effective}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-muted-foreground">
            <Link
              to="/organizations/$orgId"
              params={{ orgId }}
              search={{ tab: 'overview' }}
              className="text-primary hover:underline"
            >
              契約組織の詳細へ
            </Link>
          </p>

          {editOpen ? <EditOrgAiDialog detail={d} onClose={() => setEditOpen(false)} /> : null}
        </div>
      ) : null}
    </Section>
  );
}

/** 希望と実効の比較行を作る。 */
function policyRows(
  requested: OrgAiPolicy,
  effective: OrgAiPolicy,
): Array<{ key: string; requested: string; effective: string; differs: boolean }> {
  const cap = (n: number): string => (n === 0 ? '無制限' : formatCount(n));
  const features = (p: OrgAiPolicy): string =>
    AI_FEATURE.filter((f) => p.features[f]).map((f) => labelOf(f)).join(' / ') || 'すべて無効';

  const rows = [
    { key: '有効', requested: requested.enabled ? '有効' : '無効', effective: effective.enabled ? '有効' : '無効' },
    { key: 'プロバイダ', requested: labelOf(requested.provider), effective: labelOf(effective.provider) },
    {
      key: 'モデル',
      requested: requested.model === '' ? '共有の既定' : requested.model,
      effective: effective.model === '' ? '共有の既定' : effective.model,
    },
    {
      key: '接続先',
      requested: requested.endpoint === '' ? '—' : requested.endpoint,
      effective: effective.endpoint === '' ? '—' : effective.endpoint,
    },
    { key: '機能', requested: features(requested), effective: features(effective) },
    {
      key: 'プライバシー水準',
      requested: labelOf(requested.privacyLevel),
      effective: labelOf(effective.privacyLevel),
    },
    { key: '月間トークン上限', requested: cap(requested.monthlyTokenCap), effective: cap(effective.monthlyTokenCap) },
    { key: '上限到達時', requested: labelOf(requested.onLimit), effective: labelOf(effective.onLimit) },
    {
      key: '推論ログの保持',
      requested: requested.logRetentionDays === 0 ? '運営の既定' : `${requested.logRetentionDays} 日`,
      effective: effective.logRetentionDays === 0 ? '運営の既定' : `${effective.logRetentionDays} 日`,
    },
  ];
  return rows.map((r) => ({ ...r, differs: r.requested !== r.effective }));
}

/** 運営による上書き（`PUT /ops/ai/orgs/:orgId`）。 */
function EditOrgAiDialog({ detail, onClose }: { detail: OpsAiOrgDetail; onClose: () => void }): React.ReactElement {
  const qc = useQueryClient();
  const [policy, setPolicy] = React.useState<OrgAiPolicy>(detail.requested);
  const [lockedByOps, setLockedByOps] = React.useState(detail.lockedByOps);
  const [opsNote, setOpsNote] = React.useState(detail.opsNote ?? '');
  const [apiKey, setApiKey] = React.useState('');

  const byo = isByo(policy.provider);

  const mut = useMutation({
    mutationFn: () =>
      api.put(`/ai/orgs/${detail.organization.id}`, {
        enabled: policy.enabled,
        provider: policy.provider,
        model: policy.model,
        /*
         * **BYO 以外に endpoint を送ると 400。** 画面でも入力を隠しているので、
         * 切り替えたときに前の値が残らないよう空にして送る。
         */
        endpoint: byo ? policy.endpoint : '',
        features: policy.features,
        privacyLevel: policy.privacyLevel,
        monthlyTokenCap: policy.monthlyTokenCap,
        onLimit: policy.onLimit,
        logRetentionDays: policy.logRetentionDays,
        lockedByOps,
        // ロックを外すときは理由も消す（サーバーが null を書く）。
        ...(lockedByOps ? { opsNote: opsNote.trim() } : {}),
        // 鍵は BYO のときだけ・入力があるときだけ送る（それ以外は 400）。
        ...(byo && apiKey.length > 0 ? { apiKey: apiKey.trim() } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'ai'] });
      onClose();
    },
  });

  // サーバー側の検証と同じ条件で先に止める。
  const invalid =
    (policy.enabled && policy.provider === 'none') ||
    (lockedByOps && opsNote.trim().length === 0) ||
    (byo && policy.endpoint !== '' && !/^https?:\/\//.test(policy.endpoint));

  return (
    <FormDialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="運営として上書きする"
      description="プランの制約に関わらず運営が値を入れられます。ロックすると契約者は変更できません。保存すると同期を積みます。"
      submitLabel="保存する"
      pending={mut.isPending}
      error={mut.error}
      disabled={invalid}
      onSubmit={() => mut.mutate()}
      wide
    >
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={policy.enabled}
          onChange={(e) => setPolicy({ ...policy, enabled: e.target.checked })}
          className="size-4 rounded border-border"
        />
        AI 機能を有効にする
      </label>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="プロバイダ"
          required
          error={policy.enabled && policy.provider === 'none' ? '有効にするにはプロバイダを選んでください。' : undefined}
        >
          <NativeSelect
            value={policy.provider}
            onChange={(v) => setPolicy({ ...policy, provider: v as AiProvider })}
            options={AI_PROVIDER.map((p) => ({ value: p, label: labelOf(p) }))}
          />
        </Field>
        <Field label="モデル" hint="空なら共有の既定モデル。">
          <Input value={policy.model} onChange={(e) => setPolicy({ ...policy, model: e.target.value })} />
        </Field>
      </div>

      {byo ? (
        <>
          <Field
            label="接続先 URL"
            hint="OpenAI 互換 / Ollama のときだけ指定できます。SSRF 対策の検査を通ります。"
            error={
              policy.endpoint !== '' && !/^https?:\/\//.test(policy.endpoint)
                ? 'http(s) から始まる URL を入力してください。'
                : undefined
            }
          >
            <Input
              value={policy.endpoint}
              onChange={(e) => setPolicy({ ...policy, endpoint: e.target.value })}
              spellCheck={false}
              placeholder="https://llm.example.com/v1"
            />
          </Field>
          <Field
            label="この組織の API キー"
            hint={
              detail.credential.hasKey
                ? `登録済み（指紋 ${detail.credential.fingerprint ?? ''}）。空のままなら変更しません。`
                : '空のままなら登録しません。'
            }
          >
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
        </>
      ) : null}

      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">使ってよい機能</p>
        <div className="flex flex-wrap gap-3">
          {AI_FEATURE.map((f) => (
            <label key={f} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={policy.features[f]}
                onChange={(e) => setPolicy({ ...policy, features: { ...policy.features, [f]: e.target.checked } })}
                className="size-4 rounded border-border"
              />
              {labelOf(f)}
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="プライバシー水準" hint="どこまでを LLM へ渡すか。">
          <NativeSelect
            value={policy.privacyLevel}
            onChange={(v) => setPolicy({ ...policy, privacyLevel: v as OrgAiPolicy['privacyLevel'] })}
            options={AI_PRIVACY_LEVEL.map((p) => ({ value: p, label: labelOf(p) }))}
          />
        </Field>
        <Field label="月間トークン上限" hint="0 は無制限。プランの上限で切り詰められます。">
          <Input
            value={String(policy.monthlyTokenCap)}
            onChange={(e) =>
              setPolicy({ ...policy, monthlyTokenCap: Number(e.target.value.replace(/[^0-9]/g, '') || '0') })
            }
            inputMode="numeric"
          />
        </Field>
        <Field label="上限に達したら" hint="「従量課金」は超過分を請求します。">
          <NativeSelect
            value={policy.onLimit}
            onChange={(v) => setPolicy({ ...policy, onLimit: v as OrgAiPolicy['onLimit'] })}
            options={AI_LIMIT_ACTION.map((a) => ({ value: a, label: labelOf(a) }))}
          />
        </Field>
      </div>

      <Field label="推論ログの保持日数" hint="0 なら運営の既定。本文は元から保存していません（回数と時刻の記録）。">
        <Input
          value={String(policy.logRetentionDays)}
          onChange={(e) =>
            setPolicy({ ...policy, logRetentionDays: Number(e.target.value.replace(/[^0-9]/g, '') || '0') })
          }
          inputMode="numeric"
        />
      </Field>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={lockedByOps}
          onChange={(e) => setLockedByOps(e.target.checked)}
          className="size-4 rounded border-border"
        />
        契約者に変更させない（ロックする）
      </label>

      {lockedByOps ? (
        <Field label="ロックの理由" required hint="契約者からの問い合わせに答えるための記録です。">
          <Textarea value={opsNote} onChange={(e) => setOpsNote(e.target.value)} rows={2} />
        </Field>
      ) : null}

      <Alert>
        <AlertDescription>
          ロックを外すと、<strong>運営が入れた値がそのまま残ります</strong>（契約者の以前の希望は復活しません）。
          解除した瞬間に外部 API へ本文が流れ始める、という事故を避けるための仕様です。
        </AlertDescription>
      </Alert>
    </FormDialog>
  );
}
